/**
 * A document or paper row on the subject screen (design v2 screen 4), split the way the row
 * draws it: a meta line under the name, a pill for the state, a 4 px bar for work in motion,
 * and which action it offers. In motion is outlined; settled is filled (components.css).
 */
import type { Doc, PaperRow } from "../api/api";
import { madeAt, paperDetails, paperTitle } from "../papers/identity";
import { paperFailure, rejectMessage } from "../text/errors";

export type PillKind = "plain" | "building" | "check" | "ready" | "failed";
export type RowPill = { kind: PillKind; label: string };
export type Bar = { fraction: number; tone: "ink" | "accent" };
export type DocRow = {
  meta: string;
  danger: boolean;
  pill: RowPill | null;
  bar: Bar | null;
  action: "study" | "review" | null;
};

const mb = (bytes: number) => (bytes / 1_000_000).toFixed(1);

function minutesLeft(seconds: number): string {
  const m = Math.max(1, Math.ceil(seconds / 60));
  return `about ${m} ${m === 1 ? "minute" : "minutes"} left`;
}

export function docRow(
  d: Doc,
  extra: {
    /** The ready book's chapters, when the screen has read them. */
    chapters?: { ingested: number; total: number };
    /** The share of the bytes posted so far, while this browser uploads it. */
    uploaded?: number;
    /** The subject's format was learned from this past paper. */
    formatSource?: boolean;
  },
): DocRow {
  const kind = d.kind === "textbook" ? "Textbook" : "Past paper";
  const row = (over: Partial<DocRow>): DocRow => ({
    meta: kind,
    danger: false,
    pill: null,
    bar: null,
    action: null,
    ...over,
  });

  if (d.status === "pending") {
    if (extra.uploaded !== undefined) {
      const f = Math.min(1, Math.max(0, extra.uploaded));
      return row({
        meta: `${kind} · ${mb(d.size_bytes * f)} of ${mb(d.size_bytes)} MB`,
        pill: { kind: "plain", label: `Uploading · ${Math.round(f * 100)}%` },
        bar: { fraction: f, tone: "ink" },
      });
    }
    return row({
      meta: `${kind} · ${mb(d.size_bytes)} MB`,
      pill: { kind: "plain", label: "Checking the file" },
    });
  }
  if (d.status === "rejected")
    return row({
      meta: rejectMessage(d.reject_reason),
      danger: true,
      pill: { kind: "failed", label: "Rejected" },
    });
  if (d.status === "withdrawn") return row({ meta: `${kind} · withdrawn` });

  const pages = `${kind} · ${d.page_count} pages`;
  if (d.kind !== "textbook")
    return row({ meta: extra.formatSource ? `${pages} · format learned` : pages });

  switch (d.book_status) {
    case null:
    case "detecting":
      return row({ meta: pages, pill: { kind: "plain", label: "Finding chapters…" } });
    case "review":
      return row({
        meta: `${pages} · nothing spent until you confirm`,
        pill: { kind: "check", label: "Chapters need checking" },
        action: "review",
      });
    case "confirmed":
      return row({
        meta: `${pages} · your flashcards are on the way`,
        pill: { kind: "plain", label: "Queued" },
      });
    case "ingesting": {
      const p = d.progress;
      if (!p) return row({ meta: pages, pill: { kind: "building", label: "Making flashcards" } });
      const rest =
        p.chapters_done >= p.chapters_total ? "finishing up" : minutesLeft(p.eta_seconds);
      return row({
        meta: `${pages} · ${rest}`,
        pill: {
          kind: "building",
          label: `Making flashcards · ${p.chapters_done} of ${p.chapters_total}`,
        },
        bar: {
          fraction: p.chapters_total ? p.chapters_done / p.chapters_total : 0,
          tone: "accent",
        },
      });
    }
    case "ready": {
      const c = extra.chapters;
      return row({
        meta: c ? `${pages} · flashcards for ${c.ingested} of ${c.total} chapters` : pages,
        pill: { kind: "ready", label: "Ready to study" },
        action: "study",
      });
    }
    case "failed":
      return row({
        meta: "The flashcards could not be finished. Check the chapters and try again; the pages were not charged.",
        danger: true,
        pill: { kind: "failed", label: "Needs attention" },
        action: "review",
      });
  }
}

export type PaperView = { title: string; meta: string; danger: boolean; pill: RowPill | null };

/** "just started" in the first minute, then whole minutes; no estimate of what is left. */
function soFar(createdAt: string, now: number): string {
  const m = Math.floor((now - Date.parse(createdAt)) / 60_000);
  return m < 1 ? "just started" : `${m} min so far`;
}

export function paperRow(p: PaperRow, now: number = Date.now()): PaperView {
  // Two papers on the same chapters, at the same length, on the same day differ by number
  // and by time. With a name, the length and chapters move into the meta line.
  const title = paperTitle(p);
  const lead = p.name ? `${paperDetails(p)} · ` : "";
  switch (p.status) {
    case "requested":
      return {
        title,
        meta: `${lead}Waiting to start`,
        danger: false,
        pill: { kind: "plain", label: "Queued" },
      };
    case "generating":
      return {
        title,
        // One section is the whole of a short paper: its count would never move.
        meta:
          (p.sections_total ?? 0) > 1
            ? `${lead}Writing your paper… ${soFar(p.created_at, now)} · section ${p.sections_done ?? 0} of ${p.sections_total} checked`
            : `${lead}Writing your paper… ${soFar(p.created_at, now)}`,
        danger: false,
        pill: { kind: "plain", label: "Writing" },
      };
    case "ready":
      return {
        title,
        meta: `${lead}${p.total_marks} marks · ${p.question_count} questions · ${madeAt(p.created_at)}${
          p.error ? ` · ${p.error}` : ""
        }`,
        danger: false,
        pill: null,
      };
    case "failed":
      return {
        title,
        meta: `${lead}Could not be written.${p.error ? ` ${paperFailure(p.error)}` : ""} Not charged.`,
        danger: true,
        pill: null,
      };
  }
}

/**
 * Whether "Your materials" opens on its own (bug fix 1, spec 5 §3): when uploading is the
 * task (nothing yet), when something is moving, or when something needs the student — a book
 * to check or that failed, a file refused, a past paper not matched, a format being learned,
 * to check, or that could not be read. Settled, it folds to one line.
 */
export function materialsNeedAttention(
  docs: Doc[],
  profile: { status: string } | null,
  learning: boolean,
  unmatched: Doc[] = [],
): boolean {
  if (docs.length === 0) return true;
  if (learning || (profile && profile.status !== "confirmed")) return true;
  if (unmatched.some((d) => d.status === "uploaded" && d.described_at)) return true;
  return docs.some(
    (d) =>
      d.status === "pending" ||
      d.status === "rejected" ||
      (d.kind === "textbook" && d.status === "uploaded" && d.book_status !== "ready"),
  );
}

/** "1 textbook · 11 past papers · format confirmed": the folded line. */
export function materialsSummary(
  docs: Doc[],
  pastPapers: number,
  profile: { status: string } | null,
): string {
  const books = docs.filter((d) => d.kind === "textbook" && d.status === "uploaded").length;
  const parts = [
    `${books} textbook${books === 1 ? "" : "s"}`,
    `${pastPapers} past paper${pastPapers === 1 ? "" : "s"}`,
    profile?.status === "confirmed"
      ? "format confirmed"
      : profile?.status === "draft"
        ? "format to check"
        : "no format yet",
  ];
  return parts.join(" · ");
}
